/**
 * Subcategory Navigation
 *
 * Standard link navigation (no AJAX). Active states are handled by Liquid.
 *
 * Enhancement: client-side dedup for product-fallback images so siblings whose
 * first product is shared (e.g. Boys Pants + Boys Sportwear both lead with the
 * same Boys Sports Pant) don't render the same card image. We fetch a small
 * window of products from the duplicate collection via Shopify's public
 * /collections/<handle>/products.json endpoint and swap to the first product
 * whose image isn't already used by a sibling.
 *
 * For the archived AJAX version, see: subcategory-nav.ajax.js.archived
 */

if (!customElements.get('subcategory-nav')) {
  class SubcategoryNav extends HTMLElement {
    connectedCallback() {
      // Auto-shrink long labels on banner cards so they fit without ellipsis
      if (this.classList.contains('subcategory-nav--banners')) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.querySelectorAll('.subcategory-nav__label').forEach((label) => {
              const minFont = 8;
              let size = parseFloat(getComputedStyle(label).fontSize);
              while (label.scrollWidth > label.clientWidth && size > minFont) {
                size -= 0.5;
                label.style.fontSize = size + 'px';
              }
            });
          });
        });
      }

      this.dedupImages().catch(() => {});
    }

    async dedupImages() {
      const links = this.querySelectorAll('.subcategory-nav__link[data-subcategory-link]');
      if (!links.length) return;

      const fileNameOf = (url) => {
        if (!url) return '';
        try {
          return (url.split('/').pop() || '').split('?')[0];
        } catch {
          return '';
        }
      };

      // Group items by their rendered image filename
      const fnToItems = new Map();
      for (const link of links) {
        const img = link.querySelector('img');
        if (!img || !img.src) continue;
        const fn = fileNameOf(img.src);
        if (!fn) continue;
        if (!fnToItems.has(fn)) fnToItems.set(fn, []);
        fnToItems.get(fn).push({ link, img });
      }

      // Seed the "used" set with one entry per group (the first occurrence)
      const usedFn = new Set(fnToItems.keys());

      for (const [, items] of fnToItems) {
        if (items.length < 2) continue;
        for (let i = 1; i < items.length; i++) {
          const { link, img } = items[i];
          const href = link.getAttribute('href') || '';
          const handle = href.split('/collections/').pop().split('/')[0].split('?')[0];
          if (!handle) continue;
          try {
            const r = await fetch(`/collections/${handle}/products.json?limit=10`, {
              headers: { 'Accept': 'application/json' }
            });
            if (!r.ok) continue;
            const j = await r.json();
            const chosen = (j.products || []).find((p) => {
              const ps = p.images && p.images[0] && p.images[0].src;
              if (!ps) return false;
              return !usedFn.has(fileNameOf(ps));
            });
            if (chosen) {
              const newSrc = chosen.images[0].src;
              usedFn.add(fileNameOf(newSrc));
              const sep = newSrc.includes('?') ? '&' : '?';
              img.src = newSrc + sep + 'width=300';
              img.removeAttribute('srcset');
            }
          } catch {
            // swallow; leaves the duplicate in place rather than breaking the nav
          }
        }
      }
    }
  }

  customElements.define('subcategory-nav', SubcategoryNav);
}
